/**
 * Responsibility: represent terrain tiles as objects in the Three.js scene.
 *
 * This is the render-side half of tile streaming. `world/tileManager.js` decides which
 * tiles should exist; this turns those decisions into scene graph changes and back again.
 * Splitting it this way is what lets the residency logic be tested in Node — the manager
 * calls `mount` and `unmount` without ever learning that meshes are involved.
 *
 * Replaces `vista.js`, which built a fixed grid of tiles for the Task 3 look checkpoint.
 */

import * as THREE from 'three';

import {
  createTileMaterial,
  createTileMesh,
  createMaskMaterial,
  createMaskMesh,
  disposeTileMesh,
} from './tileMesh.js';
import { patchMaterialForRadialFog } from './fog.js';

/**
 * Create the scene-side terrain container.
 *
 * @param {THREE.Scene} scene
 * @returns {{mount: Function, unmount: Function, dispose: () => void}}
 *   `mount` and `unmount` are shaped to be handed straight to `createTileManager`.
 */
export function createTerrainView(scene) {
  if (!scene) {
    throw new Error('createTerrainView: a scene is required');
  }

  /*
    One material for every tile. They are visually identical, so a material each would
    multiply shader program switches for no benefit. It is owned here and outlives every
    individual tile, which is why `disposeTileMesh` deliberately leaves it alone.
  */
  /*
    Radial fog, so the fade boundary is the same shape as the tile loading boundary. Without it,
    terrain arrives at the left and right screen edges still partly visible — see `fog.js`.
  */
  const material = patchMaterialForRadialFog(createTileMaterial());

  /*
    Both materials are owned here and outlive every individual tile, which is why
    `disposeTileMesh` deliberately leaves materials alone.
  */
  const maskMaterial = createMaskMaterial();

  /*
    A single group holding all tiles, so terrain can be shown, hidden, or repositioned as
    one object. Task 9's floating origin will shift this group rather than every mesh.
  */
  const group = new THREE.Group();
  scene.add(group);

  return {
    /**
     * Add one tile's geometry to the scene.
     *
     * A tile can be up to two meshes: the visible lines, and an invisible depth mask that makes
     * nearer geometry hide what is behind it. They are returned together so `unmount` releases
     * both — a mask left behind would keep occluding terrain that no longer exists.
     *
     * @param {object} tileData - From `tileContent.build`.
     * @returns {{lines: THREE.LineSegments, mask: THREE.Mesh|null}} Handle the manager stores.
     */
    mount(tileData) {
      const lines = createTileMesh(tileData, material);
      group.add(lines);

      const mask = createMaskMesh(
        tileData.mask,
        maskMaterial,
        tileData.originX,
        tileData.originZ,
      );
      if (mask) {
        group.add(mask);
      }

      return { lines, mask };
    },

    /**
     * Remove one tile from the scene and release its GPU memory.
     *
     * Both steps are required. Removing without disposing leaks video memory steadily
     * until the tab dies; disposing without removing leaves a dead node in the graph.
     *
     * @param {{lines: THREE.LineSegments, mask: THREE.Mesh|null}} handle
     */
    unmount(handle) {
      group.remove(handle.lines);
      disposeTileMesh(handle.lines);

      if (handle.mask) {
        group.remove(handle.mask);
        disposeTileMesh(handle.mask);
      }
    },

    /**
     * Tear down the container itself. Individual tiles are the manager's to release, so
     * call `tileManager.dispose()` before this.
     */
    dispose() {
      scene.remove(group);
      material.dispose();
      maskMaterial.dispose();
    },
  };
}
